import React, { ReactNode, FC, useCallback, useMemo, useState, useEffect, ComponentType } from 'react';

import PageErrorBoundary, { PageErrorBoundaryProps } from 'ia-react-core/~/components/error/PageErrorBoundary';
import Loader from '../Loader';
import useTranslation from '../../../hooks/useTranslation';

export interface PageLoaderProps extends Omit<PageErrorBoundaryProps, 'children'> {
  children?: ReactNode | (() => ReactNode);
  /** Function returning a dynamic import loading the page. */
  loader: () => Promise<{ default: ComponentType<any> }>;
  /** Custom error message. */
  errorMessage?: string;
  /** Custom retry button text. */
  errorRetryButton?: string;
  /** Any props from PageErrorBoundary Replace the custom loader */
  customLoader?: ReactNode | (() => ReactNode);
}

/**
 * @mode developer
 */
const PageLoader: FC<PageLoaderProps> = ({ children, loader, errorTitle, errorMessage, errorRetryButton, customLoader, ...props }) => {
  const { t } = useTranslation('PageLoader');
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const [CustomLoadable, setCustomLoadable] = useState<ComponentType<any>>();

  const load = useCallback(async () => {
    setHasLoadingError(false);
    const { default: Component } = await loader();
    setCustomLoadable(() => Component);
  }, [loader]);

  useEffect(() => {
    load().catch(() => {
      setHasLoadingError(true);
    });
  }, [load]);

  const handleClick = useCallback(() => window.location.reload(), []);

  const indicator = useMemo(() => {
    if (customLoader) return typeof customLoader === 'function' ? customLoader() : customLoader;
    return <Loader />;
  }, [customLoader]);

  return (
    <PageErrorBoundary
      errorTitle={(typeof errorTitle !== 'undefined') ? errorTitle : t('error.title')}
      message={errorMessage}
      retryButton={(typeof errorRetryButton !== 'undefined') ? errorRetryButton : t('error.retryButton')}
      onRetry={handleClick}
      forceError={hasLoadingError}
      {...props}
    >
      {CustomLoadable ? (
        <CustomLoadable {...props}>
          {typeof children === 'function' ? children() : children}
        </CustomLoadable>
      ) : <div>{indicator}</div>}
    </PageErrorBoundary>
  );
};

export default PageLoader;
